/**
 * Where a published Terminal build actually lives.
 *
 * The storage service has no public hostname — the gateway is the only public
 * door — so a build is reached through the same-origin proxy, exactly like an
 * avatar (`utils/avatarUrl.ts`). A catalogue row therefore carries a **stored
 * path**, never a URL: a path we can check, and a URL we would have to trust.
 *
 * Since Phase 1a the source is the **release catalogue** (TERMINAL.md §17.2),
 * not a hand-written manifest: the same rows the app itself updates from, so
 * this page cannot drift from what a holder is actually offered.
 */

/** The catalogue's public read, through the web app's own proxy. */
export const TERMINAL_RELEASES_ENDPOINT = '/api/terminal/releases?channel=stable'

/** The platforms a build is offered for, in the order they are shown. */
export const PLATFORM_ORDER = ['mac', 'windows', 'linux'] as const
export type TerminalPlatform = (typeof PLATFORM_ORDER)[number]

export const PLATFORM_LABEL: Record<TerminalPlatform, string> = {
  mac: 'macOS',
  windows: 'Windows',
  linux: 'Linux'
}

/** The catalogue speaks `macos`; the page has always said `mac`. */
const PLATFORM_FROM_CATALOGUE: Record<string, TerminalPlatform> = {
  macos: 'mac',
  windows: 'windows',
  linux: 'linux'
}

export const ARCH_LABEL: Record<string, string> = {
  arm64: 'Apple silicon',
  x64: 'Intel / x64'
}

export interface TerminalDownload {
  platform: TerminalPlatform
  /** Shown beside the platform, e.g. "Apple silicon" or "Intel / x64". */
  arch?: string
  /** Stored path under the storage service, e.g. `assets/terminal/0.3.0/Terminal-…dmg`. */
  path: string
  /** Bytes, when the catalogue states it. Absent is absent — never rendered as 0. */
  sizeBytes?: number
}

export interface TerminalRelease {
  version: string
  publishedAt?: string
  /** What changed, in the holder's words. */
  notes?: string
  downloads: TerminalDownload[]
}

/**
 * Three answers, and they are deliberately different:
 *
 *  - `published`  — there is a build, here it is.
 *  - `none`       — the platform answered, and there is no build yet.
 *  - `unreadable` — we could not find out. **Not the same as "none"**: telling
 *                   somebody no build exists when we simply failed to ask is
 *                   the "unknown is never zero" mistake this codebase has
 *                   already paid for five times.
 */
export type ReleaseState =
  | { status: 'loading' }
  | { status: 'published'; release: TerminalRelease }
  | { status: 'none' }
  | { status: 'unreadable'; reason: string }

/**
 * `assets/terminal/<version>/<file>` and nothing else.
 *
 * The segment pattern starts with an alphanumeric on purpose: `[A-Za-z0-9._-]+`
 * on its own happily matches `..`, which would have let a crafted row walk out
 * of the assets tree through the storage proxy. Caught by the test that tries it.
 */
const STORED_PATH = /^assets\/terminal\/[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._-]*$/

/** A stored path becomes a same-origin URL; anything else is not rendered. */
export function downloadUrl(path: string): string | undefined {
  const clean = (path ?? '').trim().replace(/^\/+/, '')
  if (!STORED_PATH.test(clean)) return undefined
  return `/api/storage/${clean}`
}

export function formatSize(bytes?: number): string | undefined {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) return undefined
  const mb = bytes / (1024 * 1024)
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`
}

/** Parse a catalogue row defensively: an answer over the wire is data, not a promise. */
export function parseRelease(raw: unknown): TerminalRelease | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const version = typeof row.version === 'string' ? row.version.trim() : ''
  if (!version) return null

  const list = Array.isArray(row.artifacts) ? row.artifacts : []
  const downloads: TerminalDownload[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const a = entry as Record<string, unknown>
    // Only installers are offered here. A renderer bundle is something the app
    // fetches for itself; handing one to a person would be a file they cannot use.
    if (a.kind !== 'installer') continue
    const platform = PLATFORM_FROM_CATALOGUE[String(a.platform)]
    if (!platform) continue
    const path = typeof a.path === 'string' ? a.path : ''
    if (!downloadUrl(path)) continue
    const arch = typeof a.arch === 'string' ? (ARCH_LABEL[a.arch] ?? a.arch) : undefined
    downloads.push({
      platform,
      path,
      ...(arch ? { arch } : {}),
      ...(typeof a.byteSize === 'number' ? { sizeBytes: a.byteSize } : {})
    })
  }
  if (downloads.length === 0) return null

  downloads.sort(
    (a, b) =>
      PLATFORM_ORDER.indexOf(a.platform) - PLATFORM_ORDER.indexOf(b.platform) ||
      (a.arch ?? '').localeCompare(b.arch ?? '')
  )
  return {
    version,
    ...(typeof row.publishedAt === 'string' ? { publishedAt: row.publishedAt } : {}),
    ...(typeof row.notes === 'string' && row.notes.trim() ? { notes: row.notes.trim() } : {}),
    downloads
  }
}

export async function fetchRelease(signal?: AbortSignal): Promise<ReleaseState> {
  let response: Response
  try {
    response = await fetch(TERMINAL_RELEASES_ENDPOINT, { signal })
  } catch (error) {
    return { status: 'unreadable', reason: (error as Error)?.message ?? 'network error' }
  }
  if (response.status === 404) return { status: 'none' }
  if (!response.ok) return { status: 'unreadable', reason: `HTTP ${response.status}` }

  let body: unknown
  try {
    body = await response.json()
  } catch {
    return { status: 'unreadable', reason: 'the catalogue could not be read' }
  }
  // The catalogue answers newest first; an empty list is a real "none".
  const rows = Array.isArray(body) ? body : []
  for (const row of rows) {
    const release = parseRelease(row)
    if (release) return { status: 'published', release }
  }
  return { status: 'none' }
}

// ---------------------------------------------------------------------------
// Which build is this visitor's

/**
 * The visitor's platform, from what the browser will actually tell us.
 *
 * When it cannot be told, the page lists everything and calls nothing "yours"
 * — a wrong guess sends somebody to a file that will not open, which is worse
 * than one more click.
 */
export function detectPlatform(nav?: Navigator): TerminalPlatform | undefined {
  const n = nav ?? (typeof navigator === 'undefined' ? undefined : navigator)
  if (!n) return undefined
  const uaData = (n as Navigator & { userAgentData?: { platform?: string } }).userAgentData
  const hint = (uaData?.platform ?? '').toLowerCase()
  const ua = (n.userAgent ?? '').toLowerCase()
  const haystack = `${hint} ${ua}`
  if (haystack.includes('mac')) return 'mac'
  if (haystack.includes('win')) return 'windows'
  if (haystack.includes('linux') || haystack.includes('x11')) return 'linux'
  return undefined
}

export interface GroupedDownloads {
  mine: TerminalDownload[]
  others: TerminalDownload[]
}

/** Splits the builds into "yours" and "other systems", keeping the order. */
export function groupForVisitor(
  downloads: TerminalDownload[],
  platform: TerminalPlatform | undefined
): GroupedDownloads {
  if (!platform) return { mine: [], others: downloads }
  return {
    mine: downloads.filter((d) => d.platform === platform),
    others: downloads.filter((d) => d.platform !== platform)
  }
}
